import { arrayify } from 'shared/src/utils/arrayify';
import Music from './music';
import Renderer from './renderer';
import TimeSignature from './timeSignature';

interface Dimensions {
  xMin: number | false;
  yMin: number | false;
  xMax: number | false;
  yMax: number | false;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function getRhythmHTML({
  rhythmString = '',
  timeSignatures = [],
  color = '#000',
  stroke = null,
}: {
  rhythmString?: string;
  timeSignatures?: string[];
  color?: string;
  stroke?: string | null;
} = {}): string {
  const timeSignaturesHTML: string = arrayify(timeSignatures)
    .map((ts) => TimeSignature.toCSSString(ts))
    .join('\n');

  let music: Music | undefined;
  if (rhythmString) {
    music = Music.fromRhythmString(rhythmString, { noteName: 'E4' });
    // If all the notes are whole notes or larger (i.e., no stems), then set the
    // positioning as B4.
    if (
      music.voices
        .map((v) => v.measures)
        .flat()
        .map((m) => m.noteHolders)
        .flat()
        .filter((nh) => nh.rhythm)
        .every((nh) => nh.rhythm!.fraction.greaterThanEquals(1))
    ) {
      music = Music.fromRhythmString(rhythmString, { noteName: 'B4' });
    }
  }

  // If there's no window, return time signature and aria string:
  if (typeof window === 'undefined' || !music) {
    const alt =
      music?.toAriaString({
        timeSignature: false,
        letterName: false,
        accidental: false,
        octave: false,
        clef: false,
        measureNumber: false,
        barline: false,
        keySignature: false,
      }) || '';
    return `
        <span style="color: ${color}">
          ${timeSignaturesHTML}
          <span>${alt}</span>
        </span>
      `;
  }
  const svgSpan: HTMLSpanElement = document.createElement('span');
  const { svg, altDiv } = getRhythmStringSvg(music, color, stroke || undefined);
  if (svg && altDiv) {
    svg.setAttribute('role', 'img');
    svgSpan.innerHTML = '';
    if (altDiv) svgSpan.appendChild(altDiv);
    svgSpan.appendChild(svg);
  }
  return svgSpan.innerHTML;
}

function getRhythmStringSvg(music: Music, color: string, stroke?: string) {
  const throwAwayDiv = document.createElement('div');
  const renderer = new Renderer(music, throwAwayDiv, {
    ariaTitle: {
      letterName: false,
      accidental: false,
      octave: false,
      clef: false,
      measureNumber: false,
      barline: false,
      keySignature: false,
    },
  });
  renderer.ctx.save();
  renderer.ctx.attributes.fill = color;
  renderer.ctx.attributes.stroke = color;

  renderer.render();
  renderer.ctx.restore();

  const voice = music.voices[0];
  if (!voice) throw Error('Failed to generate voice for music string');
  const measure = voice.measures[0];
  if (!measure) throw Error('Failed to generate measure for music string');
  const { noteHolders } = measure;
  const voiceStaves = renderer.staves[0];
  if (!voiceStaves) throw Error('Failed to generate voiceStaves for music string');
  const stave = voiceStaves[0];
  if (!stave) throw Error('Failed to generate measure stave for music string');

  // const initialYMin: number | false = false; // stave.getYForLine(0);
  // const initialYMax: number | false = false; // stave.getYForLine(4);
  const initialYMin = stave.getYForLine(0.5);
  const initialYMax = stave.getYForLine(3.5);

  const dimensions: Dimensions = noteHolders.reduce(
    (outline: Dimensions, noteHolder) => {
      const staveNote = noteHolder.staveNote;
      if (!staveNote) return outline;
      const noteHolderBox: Box | undefined = staveNote.getBoundingBox();
      // If it's a ghost note, instead of returning a box it returns null.
      if (noteHolder.rendersAs === 'ghostNote') return outline;
      if (!noteHolderBox) return outline;
      let xMin = noteHolderBox.x;
      let xMax = noteHolderBox.x + noteHolderBox.w;
      const yMin = noteHolderBox.y;
      const yMax = noteHolderBox.y + noteHolderBox.h;

      // If it's a half or whole rest, draw a fake staff line and extend the xMax and yMax:
      if (noteHolder.isRest() && ['h', 'w'].includes(noteHolder.rhythm?.baseDuration as string)) {
        const centerX = staveNote.getCenterGlyphX();
        const w = 10;
        const x1 = centerX - w;
        const x2 = centerX + w;
        xMin = Math.min(x1, xMin);
        xMax = Math.max(x2, xMax);
        const y =
          noteHolder.rhythm?.baseDuration === 'h' ? stave.getYForLine(2) : stave.getYForLine(1);

        renderer.ctx.save();
        renderer.ctx.attributes.fill = color;
        renderer.ctx.attributes.stroke = color;
        renderer.ctx.fillRect(x1, y - 1, x2 - x1, 2);

        renderer.ctx.restore();

        // renderer.ctx.moveTo(xMin, y).lineTo(xMax, y).stroke();
      }

      return {
        xMin: outline.xMin === false || outline.xMin > xMin ? xMin : outline.xMin,
        xMax: outline.xMax === false || outline.xMax < xMax ? xMax : outline.xMax,
        yMin: outline.yMin === false || outline.yMin > yMin ? yMin : outline.yMin,
        yMax: outline.yMax === false || outline.yMax < yMax ? yMax : outline.yMax,
      };
    },
    { xMin: false, xMax: false, yMin: initialYMin, yMax: initialYMax }
  );

  const box = {
    x: dimensions.xMin || 0,
    w: 10 + (dimensions.xMax || 0) - (dimensions.xMin || 0),
    y: dimensions.yMin || 0,
    h: (dimensions.yMax || 0) - (dimensions.yMin || 0),
  };

  renderer.ctx.setViewBox(box.x, box.y, box.w, box.h);
  const { svg } = renderer.ctx;
  const altDiv = renderer.div.querySelector<HTMLDivElement>(
    `#${svg.getAttribute('aria-labelledby')}`
  );
  svg.style.display = 'inline-block';
  svg.style.height = '1.25em';
  svg.style.marginTop = '0.25em';
  if (stroke) {
    svg.style.filter = `drop-shadow(0.5px 0px 0 ${stroke})
      drop-shadow(-0.5px 0px 0 ${stroke})
      drop-shadow(0px 1px 0 ${stroke})
      drop-shadow(0px -0.5px 0 ${stroke})`;
  }

  const elem = svg.querySelector<SVGGElement>(`#vf-${stave.getAttribute('id')}`);
  if (elem) {
    elem.style.visibility = 'hidden';
  }
  return { svg, altDiv };
}
